import { isSitefinityEditor, fadeIn, fadeOut } from './helpers.js';

/****************************************************************************************************/
/* #region Globals */
/****************************************************************************************************/

console.log("Global variables initialized");

window.ww = window.innerWidth;
window.wh = window.innerHeight;
window.dh = document.documentElement.scrollHeight;
window.hh = 0;
window.st = 0;
window.lastScrollTop = 0;
window.scrollDirection = "down";
window.selectedClassName = 'selected';
window.menuOpenClassName = 'open';
window.fadeInSpeed = 50;
window.fadeOutSpeed = 75;
window.navMouseOutDelay = 600;
window.mouseOutTimer = null;
window.is_responsive = false;
window.scrollProgress = 0;

// Throttle/timing constants
const RESIZE_THROTTLE_MS = 100;
const SCROLL_TO_TOP_THRESHOLD = 300;

// Cached DOM elements
const body = document.body;
const header = document.querySelector("#site-header");
const scrollNav = document.querySelector('.scrollToTop');

export { header, scrollNav };

function updateWindowDimensions() {
    window.ww = window.innerWidth;
    window.wh = window.innerHeight;
    window.dh = document.documentElement.scrollHeight;
    window.hh = header ? header.getBoundingClientRect().height : 0;
}

// Initialize on page load
if (isSitefinityEditor()) {
    body.classList.add("loaded");
}

updateWindowDimensions();

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Scrolling */
/****************************************************************************************************/

function refreshScrollData() {
    updateScrollProgress();
    window.st = window.pageYOffset || document.documentElement.scrollTop;
    window.scrollDirection = (window.st > window.lastScrollTop) ? "down" : "up";
    window.lastScrollTop = window.st <= 0 ? 0 : window.st;
}

function updateScrollProgress() {
    const scrollableHeight = window.dh - window.wh;
    window.scrollProgress = scrollableHeight > 0
        ? (document.documentElement.scrollTop / scrollableHeight) * 100
        : 0;
}

function doScrollActions() {
    // Scroll-to-top link fade in/out
    if (scrollNav) {
        if (window.lastScrollTop > SCROLL_TO_TOP_THRESHOLD) {
            fadeIn(scrollNav, window.fadeInSpeed);
        } else {
            fadeOut(scrollNav, window.fadeOutSpeed);
        }
    }

    // Update header/body scrolling classes
    body.classList.remove("down", "up");
    body.classList.add(window.scrollDirection);
    if (header) {
        header.classList.remove("down", "up");
        header.classList.add(window.scrollDirection);
    }

    // "top" class
    if (window.lastScrollTop === 0) {
        if (!body.classList.contains("top")) {
            body.classList.add("top");
            if (header) header.classList.add("top");
        }
    } else {
        body.classList.remove("top");
        if (header) header.classList.remove("top");
    }

    // "bottom" class
    if (window.scrollProgress === 100) {
        body.classList.add("bottom");
        if (header) header.classList.add("bottom");
    } else {
        body.classList.remove("bottom");
        if (header) header.classList.remove("bottom");
    }
}

// rAF-based scroll handler — fires in sync with browser paint for zero perceived delay
let scrollPending = false;
window.addEventListener("scroll", () => {
    if (scrollPending) return;

    scrollPending = true;
    requestAnimationFrame(() => {
        refreshScrollData();
        doScrollActions();
        scrollPending = false;
    });
});

// Update dimensions on resize
let resizeTimeout = null;
window.addEventListener("resize", () => {
    if (!resizeTimeout) {
        updateWindowDimensions();
    }

    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
        updateWindowDimensions();
        if (typeof initHeaderScrub === 'function') initHeaderScrub();
        if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
        resizeTimeout = null;
    }, RESIZE_THROTTLE_MS);
});

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Popups & Modals */
/****************************************************************************************************/

// Opens any Modal widget instance by its Modal Id (see Views/Shared/Components/Modal/Default.cshtml)
window.openModal = function (modalId) {
    window.dispatchEvent(new CustomEvent('open-modal-' + modalId));
}

document.addEventListener('DOMContentLoaded', () => {
    // Modal trigger handler - matches any element with a class like "js-open-modal-{id}"
    // (e.g. a CMS Button widget with CSS class "js-open-modal-subscribe") and opens the
    // Modal widget instance configured with that same Modal Id.
    document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[class*="js-open-modal-"]');
        if (!trigger) return;

        const triggerClass = Array.from(trigger.classList).find((c) => c.startsWith('js-open-modal-'));
        if (!triggerClass) return;

        e.preventDefault();
        const modalId = triggerClass.slice('js-open-modal-'.length);
        window.openModal(modalId);
    });

    // Mobile menu handler
    document.addEventListener('click', (e) => {
        if (e.target.closest('.js-open-mobile-menu')) {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('open-mobile-menu'));
        }
    });
});

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region External Links */
/****************************************************************************************************/

function applyExternalLinkTargets(root) {
    if (isSitefinityEditor()) return;
    const currentHost = window.location.hostname;
    root.querySelectorAll('a[href]').forEach((link) => {
        try {
            const url = new URL(link.getAttribute('href'), window.location.href);
            if (url.hostname && url.hostname !== currentHost) {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');
            }
        } catch {
            // Ignore unparseable hrefs
        }
    });
}

applyExternalLinkTargets(document);

const externalLinkObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
                applyExternalLinkTargets(node);
            }
        }
    }
});

externalLinkObserver.observe(document.body, { childList: true, subtree: true });

/* #endregion */
/****************************************************************************************************/

/****************************************************************************************************/
/* #region Utilities */
/****************************************************************************************************/

if (isSitefinityEditor()) {
    console.log('Sitefinity editor detected');
    document.documentElement.setAttribute('data-sf-editor', 'true');
}

// Clickable items (e.g. a clickable article card with nested links)
document.addEventListener('click', (e) => {
    const clickable = e.target.closest('.clickable');
    if (!clickable || isSitefinityEditor()) return;

    // Allow links within to work normally
    if (e.target.closest('a[href]')) return;

    let itemUrl = '';
    let target = '_self';

    if (clickable.dataset.url?.length) {
        itemUrl = clickable.dataset.url;
        target = clickable.dataset.target || target;
    } else {
        const firstLink = clickable.querySelector('a[href]');
        if (firstLink) {
            itemUrl = firstLink.getAttribute('href');
            target = firstLink.getAttribute('target') || target;
        }
    }

    if (itemUrl) {
        if (target === '_blank') {
            window.open(itemUrl, '_blank');
        } else {
            window.location.href = itemUrl;
        }
    }
});

/* #endregion */
/****************************************************************************************************/
